import { z } from 'zod';

/**
 * Weather in the office (spec §6.11). The worker polls a provider every 15 minutes, normalises the
 * answer to `Weather` and stores it in Redis; the game server copies it into the room state.
 */
export const WEATHER_CONDITIONS = [
  'clear',
  'partly_cloudy',
  'cloudy',
  'fog',
  'drizzle',
  'rain',
  'heavy_rain',
  'thunder',
  'sleet',
  'snow',
  'heavy_snow',
] as const;
export type WeatherCondition = (typeof WEATHER_CONDITIONS)[number];
/** What clients see when no fresh reading exists (clear sky, no particles). */
export type ClientWeatherCondition = WeatherCondition | 'unknown';

export const weatherSchema = z.object({
  condition: z.enum(WEATHER_CONDITIONS),
  temperatureC: z.number(),
  windMs: z.number().nonnegative(),
  precipitationMmH: z.number().nonnegative(),
  /** When the provider's reading is valid for (ISO). */
  observedAt: z.string().datetime({ offset: true }),
  /** Which provider answered. */
  source: z.enum(['smhi', 'open-meteo']),
});
export type Weather = z.infer<typeof weatherSchema>;

/** Redis key the worker writes and the server reads. */
export const weatherKey = (worldKey: string): string => `weather:${worldKey}`;

/** Redis key of the weather of one region of the map, e.g. one office (ADR-0037). */
export const regionWeatherKey = (worldKey: string, region: string): string =>
  `weather:${worldKey}:region:${region}`;

/** Redis key where the server lists the regions with a location, for the worker to poll. */
export const weatherLocationsKey = (worldKey: string): string => `weather:${worldKey}:locations`;

/** A region of the map and where it really is. */
export const weatherLocationSchema = z.object({
  region: z.string().min(1).max(80),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
});
export type WeatherLocation = z.infer<typeof weatherLocationSchema>;

/** Parses the stored region list; malformed entries (or a malformed list) are dropped. */
export function parseWeatherLocations(raw: string | null | undefined): WeatherLocation[] {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.flatMap((entry) => {
    const result = weatherLocationSchema.safeParse(entry);
    return result.success ? [result.data] : [];
  });
}

/** A reading older than this is ignored and the weather shows as `unknown` (spec §6.11). */
export const WEATHER_MAX_AGE_MS = 3 * 60 * 60 * 1000;

/**
 * SMHI "Wsymb2" symbol codes (1-27), as used by the SMHI point forecast, to our conditions.
 * Light showers and light rain count as drizzle.
 */
const SMHI_SYMBOLS: Readonly<Record<number, WeatherCondition>> = {
  1: 'clear', // clear sky
  2: 'clear', // nearly clear sky
  3: 'partly_cloudy', // variable cloudiness
  4: 'partly_cloudy', // halfclear sky
  5: 'cloudy', // cloudy sky
  6: 'cloudy', // overcast
  7: 'fog',
  8: 'drizzle', // light rain showers
  9: 'rain', // moderate rain showers
  10: 'heavy_rain', // heavy rain showers
  11: 'thunder', // thunderstorm
  12: 'sleet', // light sleet showers
  13: 'sleet',
  14: 'sleet',
  15: 'snow', // light snow showers
  16: 'snow',
  17: 'heavy_snow',
  18: 'drizzle', // light rain
  19: 'rain',
  20: 'heavy_rain',
  21: 'thunder',
  22: 'sleet', // light sleet
  23: 'sleet',
  24: 'sleet',
  25: 'snow', // light snowfall
  26: 'snow',
  27: 'heavy_snow',
};

export function smhiSymbolToCondition(symbol: number): WeatherCondition | undefined {
  return SMHI_SYMBOLS[symbol];
}

/** WMO weather interpretation codes (Open-Meteo `weather_code`) to our conditions. */
export function wmoCodeToCondition(code: number): WeatherCondition | undefined {
  if (code === 0 || code === 1) return 'clear';
  if (code === 2) return 'partly_cloudy';
  if (code === 3) return 'cloudy';
  if (code === 45 || code === 48) return 'fog';
  if (code >= 51 && code <= 55) return 'drizzle';
  if (code === 56 || code === 57 || code === 66 || code === 67) return 'sleet';
  if (code === 61 || code === 63 || code === 80 || code === 81) return 'rain';
  if (code === 65 || code === 82) return 'heavy_rain';
  if (code === 71 || code === 73 || code === 77 || code === 85) return 'snow';
  if (code === 75 || code === 86) return 'heavy_snow';
  if (code === 95 || code === 96 || code === 99) return 'thunder';
  return undefined;
}

/** Parses a stored reading; `undefined` when missing, malformed or older than `maxAgeMs`. */
export function freshWeather(
  raw: string | null | undefined,
  now: number,
  maxAgeMs = WEATHER_MAX_AGE_MS,
): Weather | undefined {
  if (!raw) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return undefined;
  }
  const result = weatherSchema.safeParse(parsed);
  if (!result.success) return undefined;
  const age = now - Date.parse(result.data.observedAt);
  return age <= maxAgeMs ? result.data : undefined;
}
